import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImageUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fileUrl, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const TEXT_FIELDS = [
  ["company_name", "Company name"], ["industry", "Industry"], ["location", "Location"],
  ["website", "Website (https://…)"], ["contact_person", "Contact person"], ["contact_title", "Contact person's job title"],
];

export function EmployerForm({ onSaved, submitLabel = "Save" }) {
  const api = useApi();
  const { me } = useMe();
  const e0 = me.employer || {};
  const [f, setF] = useState({
    company_name: e0.company_name || "", industry: e0.industry || "", size: e0.size || "", location: e0.location || "",
    website: e0.website || "", contact_person: e0.contact_person || me.user.name || "", contact_title: e0.contact_title || "",
    about: e0.about || "", logo_file_id: e0.logo_file_id || null,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 1_000_000) return toast.error("Logo must be 1 MB or smaller");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/images", fd);
      set("logo_file_id", data.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      await api.put("/me/employer-profile", { ...f, website: f.website.trim() || null });
      await onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="employer-form">
      <div className="flex items-center gap-4">
        <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50">
          {f.logo_file_id ? <img src={fileUrl(`/api/files/${f.logo_file_id}`)} alt="Company logo" className="h-full w-full object-contain" data-testid="employer-logo-preview" />
            : <ImageUp className="h-6 w-6 text-slate-400" />}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="employer-logo-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="employer-logo-upload-btn">
            {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Upload logo
          </Button>
          <p className="mt-1 text-xs text-slate-500">PNG, JPG or WEBP, max 1 MB.</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {TEXT_FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={k}>{label}</Label>
            <Input id={k} value={f[k]} onChange={(e) => set(k, e.target.value)} maxLength={120} required={!["website", "contact_title"].includes(k)} data-testid={`employer-${k.replace(/_/g, "-")}-input`} />
          </div>
        ))}
        <div className="space-y-1.5">
          <Label>Company size</Label>
          <Select value={f.size} onValueChange={(v) => set("size", v)}>
            <SelectTrigger data-testid="employer-size-select"><SelectValue placeholder="Select size" /></SelectTrigger>
            <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s} data-testid={`employer-size-${s}`}>{s} people</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="about">About the company (optional)</Label>
        <Textarea id="about" value={f.about} onChange={(e) => set("about", e.target.value)} maxLength={1500} rows={4} data-testid="employer-about-input" />
      </div>
      <Button type="submit" disabled={busy || !f.size} className="rounded-full bg-[#222d99] px-6 hover:bg-[#1a237e]" data-testid="employer-submit-btn">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {submitLabel}
      </Button>
    </form>
  );
}

export default function EmployerOnboarding() {
  const { refresh } = useMe();
  const navigate = useNavigate();
  const saved = async () => {
    await refresh();
    toast.success("Company details submitted for review");
    navigate("/employer", { replace: true });
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white px-4 py-3 sm:px-8"><Logo /></header>
      <div className="mx-auto max-w-2xl p-4 sm:p-8">
        <p className="font-mono-label mb-2 text-xs uppercase tracking-[0.2em] text-[#222d99]">Employer setup</p>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="employer-onboarding-title">Tell us about your company</h1>
        <p className="mt-2 text-sm text-slate-600">Our team reviews every employer before candidate profiles become visible.</p>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <EmployerForm onSaved={saved} submitLabel="Submit for approval" />
        </div>
      </div>
    </div>
  );
}
